import { ed25519, x25519 } from '@noble/curves/ed25519';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils';
import { entropyToMnemonic, mnemonicToEntropy, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import type { EncryptedObject, RecoveryWrappedKeyPayload, RootKeyEnvelope } from '@trove/contracts';
import { rootKeyEnvelopeSchema } from '@trove/contracts';

const encoder = new TextEncoder();
const subtle = globalThis.crypto.subtle;

export interface DeviceKeys {
  encryptionPrivateKey: Uint8Array;
  encryptionPublicKey: Uint8Array;
  signingPrivateKey: Uint8Array;
  signingPublicKey: Uint8Array;
}

export interface WrappedKey {
  ephemeralPublicKey: string;
  nonce: string;
  ciphertext: string;
}

export const randomBytes = (length: number): Uint8Array =>
  globalThis.crypto.getRandomValues(new Uint8Array(length));

export const toBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
};

export const fromBase64 = (value: string): Uint8Array => {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
};

const importAesKey = (bytes: Uint8Array) =>
  subtle.importKey('raw', bytes as BufferSource, { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);

const hkdf = async (secret: Uint8Array, salt: Uint8Array, info: string): Promise<Uint8Array> => {
  const material = await subtle.importKey('raw', secret as BufferSource, 'HKDF', false, [
    'deriveBits',
  ]);
  const bits = await subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: salt as BufferSource, info: encoder.encode(info) },
    material,
    256,
  );
  return new Uint8Array(bits);
};

export const sha256 = async (bytes: Uint8Array): Promise<Uint8Array> =>
  new Uint8Array(await subtle.digest('SHA-256', bytes as BufferSource));

export const opaqueIdFor = async (bytes: Uint8Array): Promise<string> =>
  `obj_${bytesToHex(await sha256(bytes))}`;

export const generateDeviceKeys = (): DeviceKeys => {
  const encryptionPrivateKey = x25519.utils.randomPrivateKey();
  const signingPrivateKey = ed25519.utils.randomPrivateKey();
  return {
    encryptionPrivateKey,
    encryptionPublicKey: x25519.getPublicKey(encryptionPrivateKey),
    signingPrivateKey,
    signingPublicKey: ed25519.getPublicKey(signingPrivateKey),
  };
};

export const sign = (message: Uint8Array, privateKey: Uint8Array): Uint8Array =>
  ed25519.sign(message, privateKey);

export const verify = (
  signature: Uint8Array,
  message: Uint8Array,
  publicKey: Uint8Array,
): boolean => ed25519.verify(signature, message, publicKey);

export const wrapKey = async (
  key: Uint8Array,
  recipientPublicKey: Uint8Array,
): Promise<WrappedKey> => {
  const ephemeralPrivateKey = x25519.utils.randomPrivateKey();
  const ephemeralPublicKey = x25519.getPublicKey(ephemeralPrivateKey);
  const nonce = randomBytes(12);
  const sharedSecret = x25519.getSharedSecret(ephemeralPrivateKey, recipientPublicKey);
  const wrappingKey = await hkdf(sharedSecret, nonce, 'trove-key-wrap-v1');
  const ciphertext = await subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: nonce as BufferSource,
      additionalData: ephemeralPublicKey as BufferSource,
    },
    await importAesKey(wrappingKey),
    key as BufferSource,
  );
  return {
    ephemeralPublicKey: toBase64(ephemeralPublicKey),
    nonce: toBase64(nonce),
    ciphertext: toBase64(new Uint8Array(ciphertext)),
  };
};

export const unwrapKey = async (
  wrapped: WrappedKey,
  recipientPrivateKey: Uint8Array,
): Promise<Uint8Array> => {
  const ephemeralPublicKey = fromBase64(wrapped.ephemeralPublicKey);
  const nonce = fromBase64(wrapped.nonce);
  const sharedSecret = x25519.getSharedSecret(recipientPrivateKey, ephemeralPublicKey);
  const wrappingKey = await hkdf(sharedSecret, nonce, 'trove-key-wrap-v1');
  const plaintext = await subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: nonce as BufferSource,
      additionalData: ephemeralPublicKey as BufferSource,
    },
    await importAesKey(wrappingKey),
    fromBase64(wrapped.ciphertext) as BufferSource,
  );
  return new Uint8Array(plaintext);
};

const signedObjectBytes = (object: Omit<EncryptedObject, 'objectId' | 'signature'>): Uint8Array =>
  encoder.encode(
    JSON.stringify({
      version: object.version,
      collectionId: object.collectionId,
      kind: object.kind,
      nonce: object.nonce,
      ciphertext: object.ciphertext,
      signerPublicKey: object.signerPublicKey,
    }),
  );

export const encryptObject = async (
  plaintext: Uint8Array,
  collectionKey: Uint8Array,
  collectionId: string,
  kind: EncryptedObject['kind'],
  signingPrivateKey: Uint8Array,
  signingPublicKey: Uint8Array,
): Promise<EncryptedObject> => {
  const nonce = randomBytes(12);
  const aad = encoder.encode(`trove:${collectionId}:${kind}:v1`);
  const ciphertext = new Uint8Array(
    await subtle.encrypt(
      { name: 'AES-GCM', iv: nonce as BufferSource, additionalData: aad as BufferSource },
      await importAesKey(collectionKey),
      plaintext as BufferSource,
    ),
  );
  const unsigned = {
    version: 1 as const,
    collectionId,
    kind,
    nonce: toBase64(nonce),
    ciphertext: toBase64(ciphertext),
    signerPublicKey: toBase64(signingPublicKey),
  };
  const signature = toBase64(sign(signedObjectBytes(unsigned), signingPrivateKey));
  const objectId = await opaqueIdFor(encoder.encode(JSON.stringify({ ...unsigned, signature })));
  return { ...unsigned, signature, objectId };
};

export const decryptObject = async (
  object: EncryptedObject,
  collectionKey: Uint8Array,
): Promise<Uint8Array> => {
  const { objectId: _objectId, signature, ...unsigned } = object;
  const canonicalUnsigned = {
    version: object.version,
    collectionId: object.collectionId,
    kind: object.kind,
    nonce: object.nonce,
    ciphertext: object.ciphertext,
    signerPublicKey: object.signerPublicKey,
  };
  if (
    !verify(
      fromBase64(signature),
      signedObjectBytes(canonicalUnsigned),
      fromBase64(object.signerPublicKey),
    )
  ) {
    throw new Error('Object signature is invalid');
  }
  const expectedId = await opaqueIdFor(
    encoder.encode(JSON.stringify({ ...canonicalUnsigned, signature })),
  );
  if (expectedId !== object.objectId)
    throw new Error('Object identifier does not match ciphertext');
  const plaintext = await subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: fromBase64(object.nonce) as BufferSource,
      additionalData: encoder.encode(
        `trove:${object.collectionId}:${object.kind}:v1`,
      ) as BufferSource,
    },
    await importAesKey(collectionKey),
    fromBase64(object.ciphertext) as BufferSource,
  );
  return new Uint8Array(plaintext);
};

/** 12-word BIP39 (128-bit entropy). Hashed to 32 bytes for AES key wrap. */
export const createRecoveryMnemonic = (): string => entropyToMnemonic(randomBytes(16), wordlist);

export const recoveryKeyFromMnemonic = async (mnemonic: string): Promise<Uint8Array> => {
  if (!validateMnemonic(mnemonic, wordlist)) throw new Error('Recovery phrase is invalid');
  return sha256(mnemonicToEntropy(mnemonic, wordlist));
};

export type RecoveryWrappedKey = RecoveryWrappedKeyPayload;

export const wrapRootKeyForRecovery = async (
  rootKey: Uint8Array,
  mnemonic: string,
): Promise<RecoveryWrappedKey> => {
  const nonce = randomBytes(12);
  const recoveryKey = await recoveryKeyFromMnemonic(mnemonic);
  const ciphertext = await subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: nonce as BufferSource,
      additionalData: encoder.encode('trove-recovery-v1') as BufferSource,
    },
    await importAesKey(recoveryKey),
    rootKey as BufferSource,
  );
  return { nonce: toBase64(nonce), ciphertext: toBase64(new Uint8Array(ciphertext)) };
};

export const unwrapRootKeyFromRecovery = async (
  wrapped: RecoveryWrappedKey,
  mnemonic: string,
): Promise<Uint8Array> => {
  const nonce = fromBase64(wrapped.nonce);
  const recoveryKey = await recoveryKeyFromMnemonic(mnemonic.trim().replace(/\s+/g, ' '));
  try {
    const plaintext = await subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: nonce as BufferSource,
        additionalData: encoder.encode('trove-recovery-v1') as BufferSource,
      },
      await importAesKey(recoveryKey),
      fromBase64(wrapped.ciphertext) as BufferSource,
    );
    return new Uint8Array(plaintext);
  } catch {
    throw new Error('Recovery phrase could not unlock this repository');
  }
};

/** Wraps a vault key with the credential-scoped secret returned by WebAuthn PRF. */
export const wrapRootKeyForPasskey = async (
  rootKey: Uint8Array,
  prfSecret: Uint8Array,
  repositoryId: string,
): Promise<RecoveryWrappedKey> => {
  const nonce = randomBytes(12);
  const ciphertext = await subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: nonce as BufferSource,
      additionalData: encoder.encode(`trove-passkey:${repositoryId}:v1`) as BufferSource,
    },
    await importAesKey(prfSecret),
    rootKey as BufferSource,
  );
  return { nonce: toBase64(nonce), ciphertext: toBase64(new Uint8Array(ciphertext)) };
};

export const unwrapRootKeyFromPasskey = async (
  wrapped: RecoveryWrappedKey,
  prfSecret: Uint8Array,
  repositoryId: string,
): Promise<Uint8Array> => {
  try {
    const plaintext = await subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: fromBase64(wrapped.nonce) as BufferSource,
        additionalData: encoder.encode(`trove-passkey:${repositoryId}:v1`) as BufferSource,
      },
      await importAesKey(prfSecret),
      fromBase64(wrapped.ciphertext) as BufferSource,
    );
    return new Uint8Array(plaintext);
  } catch {
    throw new Error('Passkey could not unlock this repository');
  }
};

export const serializeWrappedKey = (wrapped: WrappedKey): string =>
  toBase64(encoder.encode(JSON.stringify(wrapped)));

export const parseWrappedKey = (value: string): WrappedKey =>
  JSON.parse(new TextDecoder().decode(fromBase64(value))) as WrappedKey;

export const parseRootKeyEnvelope = (wrappedRootKey: string): RootKeyEnvelope =>
  rootKeyEnvelopeSchema.parse(JSON.parse(new TextDecoder().decode(fromBase64(wrappedRootKey))));

export const encodeRootKeyEnvelope = (envelope: RootKeyEnvelope): string =>
  toBase64(encoder.encode(JSON.stringify(rootKeyEnvelopeSchema.parse(envelope))));

export const exportDeviceKeys = (keys: DeviceKeys) => ({
  encryptionPrivateKey: bytesToHex(keys.encryptionPrivateKey),
  encryptionPublicKey: bytesToHex(keys.encryptionPublicKey),
  signingPrivateKey: bytesToHex(keys.signingPrivateKey),
  signingPublicKey: bytesToHex(keys.signingPublicKey),
});

export const importDeviceKeys = (keys: ReturnType<typeof exportDeviceKeys>): DeviceKeys => ({
  encryptionPrivateKey: hexToBytes(keys.encryptionPrivateKey),
  encryptionPublicKey: hexToBytes(keys.encryptionPublicKey),
  signingPrivateKey: hexToBytes(keys.signingPrivateKey),
  signingPublicKey: hexToBytes(keys.signingPublicKey),
});
